import { useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import { t } from 'i18next';

import { loyaltyAPI } from '../api/loyaltyAPI';
import { toBonusOperation } from '../libs/bonusHistoryAdapter';
import { IBonusHistoryResponse, IBonusOperation } from '../types/BonusHistory';

const ITEMS_PER_PAGE = 20;

/** Границы периода — календарные дни, `YYYY-MM-DD`. */
export type BonusHistoryPeriod = { from: string; to: string };

// Бэкенд ждёт UTC без суффикса Z: «2026-07-01T00:00:00.000». Берём границы
// дня целиком, чтобы операции с самого края периода не выпали.
const toUtcBody = (day: string, endOfDay: boolean) =>
  new Date(`${day}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}Z`)
    .toISOString()
    .slice(0, -1);

const errorMessage = (error: unknown) => {
  if (axios.isAxiosError<IBonusHistoryResponse>(error)) {
    const data = error.response?.data;
    return data?.error?.message || data?.message || t('somethingWentWrong');
  }

  return error instanceof Error ? error.message : t('somethingWentWrong');
};

/**
 * История баллов постранично: `has_more` в ответе говорит, есть ли ещё.
 * Отказ бэкенд отдаёт двумя способами — 200 с `success: false` и HTTP-ошибкой
 * с тем же телом; оба сводятся к тексту для экрана.
 */
export const useBonusHistory = (period: BonusHistoryPeriod, enabled: boolean) => {
  const query = useInfiniteQuery({
    queryKey: ['bonus-history', period.from, period.to],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const res = await loyaltyAPI.getBonusHistory({
        dateFrom: toUtcBody(period.from, false),
        dateTo: toUtcBody(period.to, true),
        pageNumber: pageParam,
        itemsPerPage: ITEMS_PER_PAGE,
      });

      if (!res.data.success) {
        throw new Error(res.data.error?.message || res.data.message || t('somethingWentWrong'));
      }

      return res.data;
    },
    getNextPageParam: (last, pages) => (last.has_more ? pages.length + 1 : undefined),
    enabled,
    staleTime: 60_000,
    retry: false,
  });

  const seen = new Set<string>();
  const operations: IBonusOperation[] = (query.data?.pages ?? [])
    .flatMap(page => page.items ?? [])
    .map(toBonusOperation)
    .filter(operation => {
      if (seen.has(operation.id)) return false;
      seen.add(operation.id);
      return true;
    });

  return {
    operations,
    isLoading: query.isLoading,
    isError: query.isError,
    errorMessage: query.isError ? errorMessage(query.error) : null,
    hasMore: Boolean(query.hasNextPage),
    loadMore: query.fetchNextPage,
    isLoadingMore: query.isFetchingNextPage,
  };
};
